import React from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Dropdown } from "react-bootstrap";
import { 
    FaRegEdit, 
    FaSyncAlt, 
    FaDownload, 
    FaBell, 
    FaTag, 
    FaUserMd, 
    FaEnvelope, 
    FaComment,
    FaFileCsv,
    FaFileExcel
} from "react-icons/fa";

const TreatmentActionButton = ({ rowSelection, filteredTreatments }) => {
    const navigate = useNavigate();

    const handleExportCSV = () => {
        if (!rowSelection || Object.keys(rowSelection).length === 0) {
            alert("No treatments selected.");
            return;
        }

        const selectedTreatments = Object.keys(rowSelection)
            .map(rowId => filteredTreatments.find((treatment, index) => index === parseInt(rowId)))
            .filter(treatment => treatment !== undefined);

        if (selectedTreatments.length === 0) {
            alert("No selected treatments could be found.");
            return;
        }

        selectedTreatments.forEach(treatment => {
            const treatmentName = treatment.patientName || "treatment";
            exportToCSV([treatment], treatmentName);
        });
    };

    const exportToCSV = (treatments, treatmentName) => {
        if (!treatments || treatments.length === 0) {
            alert("No treatments selected.");
            return;
        }

        const headers = Object.keys(treatments[0]).join(',');

        const rows = treatments.map(t =>
            Object.values(t).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
        ).join('\n');

        const csv = `${headers}\n${rows}`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        const fileName = `${treatmentName}_treatment.csv`;
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportExcel = () => {
        if (!rowSelection || Object.keys(rowSelection).length === 0) {
            alert("No treatments selected.");
            return;
        }

        const selectedTreatments = Object.keys(rowSelection)
            .map(rowId => filteredTreatments.find((treatment, index) => index === parseInt(rowId)))
            .filter(treatment => treatment !== undefined);

        if (selectedTreatments.length === 0) {
            alert("No selected treatments could be found.");
            return;
        }

        selectedTreatments.forEach(treatment => {
            const treatmentName = treatment.patientName || "treatment";
            exportToExcel([treatment], treatmentName);
        });
    };

    const exportToExcel = (treatments, treatmentName) => {
        if (!treatments || treatments.length === 0) {
            alert("No treatments selected.");
            return;
        }

        const sheetData = treatments.map(treatment => {
            return Object.values(treatment);
        });

        const headers = Object.keys(treatments[0]);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...sheetData]);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Treatment");

        const fileName = `${treatmentName}_treatment.xlsx`;

        XLSX.writeFile(wb, fileName);
    };

    const handleActionClick = (action) => {
        switch (action) {
            case "Reschedule":
                console.log("Reschedule treatment");
                break;
            case "Cancel":
                console.log("Cancel treatment");
                break;
            case "Export CSV":
                handleExportCSV();
                break;
            case "Export Excel":
                handleExportExcel();
                break;
            case "Send Notification":
                console.log("Send notification");
                break;
            case "Assign Tags":
                console.log("Assign tags");
                break;
            case "Change Doctor":
                console.log("Change doctor");
                break;
            case "Update Status":
                console.log("Update status");
                break;
            case "Send Mail":
                navigate("/mail");
                break;
            case "Send Message":
                navigate("/message");
                break;
            case "Download Report":
                handleExportExcel();
                break;
            default:
                break;
        }
    };

    return (
        <div className="flex-grow-sm-0">
            <Dropdown>
                <Dropdown.Toggle
                    id="treatment-action-dropdown"
                    className={`btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn`}
                >
                    <span>Action</span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="dropdown-menu w-100 bg-white fs-sm fw-semibold border-0 rounded-3 shadow-sm mt-2">
                    <Dropdown.Item
                        onClick={() => handleActionClick("Reschedule")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaRegEdit className="icon-sm" />
                        <span>Reschedule</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Cancel")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaSyncAlt className="icon-sm" />
                        <span>Cancel</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Export CSV")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaFileCsv className="icon-sm" />
                        <span>Download CSV</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Export Excel")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaFileExcel className="icon-sm" />
                        <span>Download Excel</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Send Notification")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaBell className="icon-sm" />
                        <span>Send Notification</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Assign Tags")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaTag className="icon-sm" />
                        <span>Assign Tags</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Change Doctor")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaUserMd className="icon-sm" />
                        <span>Change Doctor</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Update Status")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaSyncAlt className="icon-sm" />
                        <span>Update Status</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Send Mail")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaEnvelope className="icon-sm" />
                        <span>Send Mail</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Send Message")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaComment className="icon-sm" />
                        <span>Send Message</span>
                    </Dropdown.Item>

                    <Dropdown.Item
                        onClick={() => handleActionClick("Download Report")}
                        className="dropdown-item d-flex align-items-center gap-2 py-2"
                    >
                        <FaDownload className="icon-sm" />
                        <span>Download Report</span>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default TreatmentActionButton;